import { BreifInfluencerV2StatusEnum } from "@entities/brief/lib/constants";
import { useAddInitialBrandCost } from "@entities/brief/lib/hooks/useAddInitialBrandCost";

import BrandCommercialsDropdown from "@features/BrandCommercialsV2/BrandCommercialsDropdown";
import { BriefTimelinePopOver } from "@features/briefTimeline/BriefTimelinePopOver";
import { AddCommercials } from "@features/InfluencerCommercialsV2/InfluencerCommercialsCost/AddCommercials";
import { NegotiateCommercials } from "@features/NegotiateCommercials";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { IBriefCampaignBriefInfluencer } from "@shared/lib/types/briefCampains";
import {
  IBrandCostRequest,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";
import { InrAmountWordsLine, Popover } from "@shared/ui";
import { Flex, Space, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

const { Text } = Typography;

interface CampaignInfluencerCommercialsProps {
  campaignInfluencer: IBriefInfluencerV2 | IBriefCampaignBriefInfluencer;

  isLastRow?: boolean;
  updateBriefInfluencer?: (id: string, data: IBriefInfluencerV2) => void;
  inBrief?: boolean;
}

interface CommercialHistoryEntry {
  date: string;
  action: string;
  amount: number;
  by: string;
  notes?: string;
  tips?: string;
  isDeliverbleChanged?: boolean;
}

export function CampaignBrandCommercials({
  campaignInfluencer,
  isLastRow = false,
  updateBriefInfluencer,
  inBrief = true,
}: CampaignInfluencerCommercialsProps) {
  const [isTargetPopoverOpen, setIsTargetPopoverOpen] = useState(false);
  const [isAddingCommercial, setIsAddingCommercial] = useState(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const { briefId } = useBriefStore();
  const { addInitialCost, isAdded, isAdding } = useAddInitialBrandCost(briefId);

  useEffect(() => {
    if (isAdded) {
      ToastService.success("Initial cost added successfully");
      setIsAddingCommercial(false);
    }
  }, [isAdded]);

  const price = campaignInfluencer.brandCost?.cost;
  const hasBrandCommercial = price !== undefined;
  const targetPrice = campaignInfluencer.targetBrandCost?.cost;
  const currency = "₹"; // Default currency, could be made configurable

  // Determine commercial status
  const commercialStatus = useMemo(() => {
    if (campaignInfluencer.isBrandDeclined) return "Declined";
    if (campaignInfluencer.isCreatorDeclined) return "Creator Declined";
    if (campaignInfluencer.status === "Purchased") return "Purchased";
    if (campaignInfluencer.isNewCommericalRequested) return "Negotiating";
    if (!price) return "Commercials Pending";
    return "Purchase";
  }, [
    campaignInfluencer.isBrandDeclined,
    campaignInfluencer.isCreatorDeclined,
    campaignInfluencer.status,
    campaignInfluencer.isNewCommericalRequested,
    price,
  ]);

  const isTargetMet = price && targetPrice && price <= targetPrice;
  const isPurchased =
    campaignInfluencer.status === BreifInfluencerV2StatusEnum.Purchased;
  const isPendingApproval = campaignInfluencer.isQueuedForPurchaseLot;

  // Build history from timeline events (using brandTimeLine like BriefTimeline)

  const getPriceColor = () => {
    if (isPurchased) return styles.pricePurchased;
    if (isPendingApproval) return styles.pricePendingApproval;
    if (isTargetMet) return styles.priceTargetMet;
    if (commercialStatus === "Negotiating") return styles.priceNegotiating;
    return styles.priceDefault;
  };

  const showBrandCommercialsDropdown: boolean = !inBrief
    ? false
    : true;
    // (!campaignInfluencer.isBrandDeclined &&
    //     !campaignInfluencer.isCreatorDeclined &&
    //     !campaignInfluencer.isDeliverablesPending &&
    //     !campaignInfluencer.isCreatorOnboardPending &&
    //     campaignInfluencer.status !== BreifInfluencerV2StatusEnum.Purchased) ||
    //   false;
  // if (campaignInfluencer?.isCreatorOnboardPending) {
  //   return <Text className={styles.pendingText}>Onboarding Pending</Text>;
  // }
  return (
    <div
      className={styles.commercialsContainer}
      onMouseEnter={() => inBrief && setIsHovered(true)}
      onMouseLeave={() => {
        if (
          !isTargetPopoverOpen &&
          !isDropdownOpen &&
          !isAddingCommercial &&
          inBrief
        ) {
          setIsHovered(false);
        }
      }}
    >
      <div className={styles.commercialsContent}>
        {hasBrandCommercial ? (
          <div className={styles.priceContainer}>
            <div className={`${styles.priceRow} ${styles.priceGroup}`}>
              <span className={`${styles.priceText} ${getPriceColor()}`}>
                {currency}
                {price}
              </span>
              {inBrief && (
                <BriefTimelinePopOver
                  briefInfluencer={campaignInfluencer}
                  type="brand"
                />
              )}
            </div>
            <InrAmountWordsLine
              value={price}
              className={styles.amountInWords}
            />

            {/* {history.length > 0 && (
                <Popover
                  content={historyContent}
                  placement={isLastRow ? "top" : "bottom"}
                  trigger={["hover", "click"]}
                >
                  <div className={styles.historyIconWrapper}>
                    <History size={12} className={styles.historyIcon} />
                  </div>
                </Popover>
              )} */}

            {!["Purchased", "Declined", "Creator Declined"].includes(
              commercialStatus
            ) &&
              !isPendingApproval &&
              updateBriefInfluencer && (
                <Popover
                  open={isTargetPopoverOpen}
                  onOpenChange={setIsTargetPopoverOpen}
                  trigger="click"
                  overlayClassName={styles.targetPopover}
                  destroyTooltipOnHide
                  content={
                    isTargetPopoverOpen ? (
                      <NegotiateCommercials
                        campaignInfluencer={campaignInfluencer}
                        onclose={() => setIsTargetPopoverOpen(false)}
                        updateBriefInfluencer={updateBriefInfluencer}
                        variant="popover"
                      />
                    ) : null
                  }
                  placement="bottomLeft"
                >
                  {/* <Button
                    type="link"
                    style={{ padding: 0, height: "max-content" }}
                    className={styles.targetButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsTargetPopoverOpen(true);
                    }}
                  >
                    Target
                  </Button> */}
                </Popover>
              )}
          </div>
        ) : (
          <Flex vertical gap={2} align="start">
            <Space size={4} align="center" style={{ height: "14px" }}>
              {showBrandCommercialsDropdown ? (
                <Popover
                  open={isAddingCommercial}
                  onOpenChange={setIsAddingCommercial}
                  trigger={["click"]}
                  title="Add Brand Commercial"
                  destroyTooltipOnHide
                  overlayClassName={styles.targetPopover}
                  content={
                    isAddingCommercial ? (
                      <AddCommercials
                        initialValues={{
                          cost: campaignInfluencer.brandCost?.cost,
                          note: campaignInfluencer.brandCost?.note,
                        }}
                        onClose={() => setIsAddingCommercial(false)}
                        handleSave={(values) => {
                          addInitialCost({
                            ...(values as Omit<
                              IBrandCostRequest,
                              "briefInfluencerId"
                            >),
                            briefInfluencerId: campaignInfluencer._id,
                            isNonCancellable:
                              (values as IBrandCostRequest).isNonCancellable ||
                              false,
                          });
                        }}
                        isLoading={isAdding}
                        briefInfluencer={campaignInfluencer}
                        type="brand"
                        isViewOnly={false}
                      />
                    ) : null
                  }
                  placement={isLastRow ? "top" : "bottom"}
                >
                  <Text
                    className={styles.pendingTextClickable}
                    onClick={(event) => event.stopPropagation()}
                  >
                    Pending
                  </Text>
                </Popover>
              ) : (
                <Text className={styles.pendingText}>Pending</Text>
              )}
              {/* {history.length > 0 && (
                <Popover
                  content={historyContent}
                  placement={isLastRow ? "top" : "bottom"}
                  trigger={["hover", "click"]}
                >
                  <div className={styles.historyIconWrapper}>
                    <History
                      size={12}
                      className={styles.historyIcon}
                      style={{ marginTop: 2 }}
                    />
                  </div>
                </Popover>
              )} */}
              {inBrief && (
                <BriefTimelinePopOver
                  briefInfluencer={campaignInfluencer}
                  type="brand"
                />
              )}
            </Space>

            {/* {updateBriefInfluencer && (
              <Popover
                open={isTargetPopoverOpen}
                onOpenChange={setIsTargetPopoverOpen}
                trigger="click"
                overlayClassName={styles.targetPopover}
                content={
                  <NegotiateCommercials
                    campaignInfluencer={campaignInfluencer}
                    onclose={() => setIsTargetPopoverOpen(false)}
                    updateBriefInfluencer={updateBriefInfluencer}
                    variant="popover"
                  />
                }
                placement="bottomLeft"
              >
                <Button
                  type="link"
                  className={styles.targetButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTargetPopoverOpen(true);
                  }}
                >
                  Target
                </Button>
              </Popover>
            )} */}
          </Flex>
        )}
        {showBrandCommercialsDropdown && hasBrandCommercial && (
          <BrandCommercialsDropdown
            briefInfluencer={campaignInfluencer}
            isHovered={isHovered}
            onDropdownOpenChange={setIsDropdownOpen}
          />
        )}
      </div>
    </div>
  );
}
